import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import NavigationBar from '../components/NavigationBar';
import { API_BASE_URL } from '../config/config';
import '../css/TeleMedicine.css';
import io from 'socket.io-client';

const TeleMedicine = () => {
    const navigate = useNavigate();
    const [meetings, setMeetings] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        date: '',
        time: '',
        participants: [],
        description: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    
    // 화상 통화 관련 state
    const [showVideoCall, setShowVideoCall] = useState(false);
    const [stream, setStream] = useState(null);
    const [isVideoOn, setIsVideoOn] = useState(true);
    const [isAudioOn, setIsAudioOn] = useState(true);
    const videoRef = useRef(null);

    // WebRTC 관련 state 추가
    const [peerConnections, setPeerConnections] = useState({});
    const [roomId, setRoomId] = useState(null);
    const [telemedicineStreams, setTelemedicineStreams] = useState({});
    
    // Socket.io 연결
    const socket = useRef(null);

    // 초대 관련 state 추가
    const [showJoinModal, setShowJoinModal] = useState(false);
    const [joinRoomId, setJoinRoomId] = useState('');

    // 1. State for dummy data and paging
    const [applyList, setApplyList] = useState([]); // 비대면 진료 신청 현황 데이터
    const [applyPage, setApplyPage] = useState(1);
    const [applyTotalPages, setApplyTotalPages] = useState(3); // 예시

    const [statusList, setStatusList] = useState([]); // 비대면 진료 현황 데이터
    const [statusPage, setStatusPage] = useState(1);
    const [statusTotalPages, setStatusTotalPages] = useState(3); // 예시

    // 2. Paging handler
    const handleApplyPageChange = (page) => setApplyPage(page);
    const handleStatusPageChange = (page) => setStatusPage(page);

    // 3. Paging component
    const Paging = ({ current, total, onChange }) => (
        <div className="telemedicine-paging">
            {Array.from({ length: total }, (_, i) => (
                <button
                    key={i+1}
                    className={`paging-btn${current === i+1 ? ' active' : ''}`}
                    onClick={() => onChange(i+1)}
                >
                    {i+1}
                </button>
            ))}
        </div>
    );

    useEffect(() => {
        // 토큰이 없으면 로그인 페이지로 리다이렉트
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }
        
        fetchMeetings();
    }, [navigate]);

    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
        }
    }, [stream]);

    useEffect(() => {
        // Socket.io 서버 연결
        socket.current = io(API_BASE_URL);
        
        // 시그널링 이벤트 리스너 설정
        socket.current.on('user-joined', handleUserJoined);
        socket.current.on('offer', handleOffer);
        socket.current.on('answer', handleAnswer);
        socket.current.on('ice-candidate', handleIceCandidate);
        
        return () => {
            if (socket.current) {
                socket.current.disconnect();
            }
        };
    }, []);

    // 카메라 및 마이크 초기화
    const initializeMedia = async () => {
        try {
            const constraints = {
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'user'
                },
                audio: true
            };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(mediaStream);
            setIsVideoOn(true);
            setIsAudioOn(true);
            return true;
        } catch (error) {
            console.error('미디어 장치 접근 실패:', error);
            alert('카메라 또는 마이크 접근에 실패했습니다.');
            return false;
        }
    };

    // 비디오 ON/OFF 토글
    const toggleVideo = () => {
        if (stream) {
            const videoTracks = stream.getVideoTracks();
            videoTracks.forEach(track => {
                track.enabled = !isVideoOn;
            });
            setIsVideoOn(!isVideoOn);
        }
    };

    // 오디오 ON/OFF 토글
    const toggleAudio = () => {
        if (stream) {
            const audioTracks = stream.getAudioTracks();
            audioTracks.forEach(track => {
                track.enabled = !isAudioOn;
            });
            setIsAudioOn(!isAudioOn);
        }
    };

    // 화상 회의 종료
    const endCall = () => {
        if (stream) {
            stream.getTracks().forEach(track => {
                track.stop();
            });
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
            setStream(null);
        }
        setShowVideoCall(false);
        setIsVideoOn(false);
        setIsAudioOn(false);
        
        // WebRTC 연결 정리
        Object.values(peerConnections).forEach(connection => {
            connection.close();
        });
        setPeerConnections({});
        setTelemedicineStreams({});
        
        // 방 나가기
        if (roomId) {
            socket.current.emit('leave-room', roomId);
            setRoomId(null);
        }
    };

    const fetchMeetings = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No token found');
            }
            
            const response = await axios.get(`${API_BASE_URL}/api/meetings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setMeetings(response.data || []); // 데이터가 없을 경우 빈 배열로 설정
        } catch (error) {
            console.error('미팅 목록 조회 실패:', error);
            if (error.response?.status === 401) {
                navigate('/');
            }
            setMeetings([]); // 에러 발생 시 빈 배열로 설정
        }
    };

    const handleSearchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No token found');
            }

            const response = await axios.get(`${API_BASE_URL}/api/users/search?term=${searchTerm}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setSearchResults(response.data || []); // 데이터가 없을 경우 빈 배열로 설정
        } catch (error) {
            console.error('사용자 검색 실패:', error);
            if (error.response?.status === 401) {
                navigate('/');
            }
            setSearchResults([]); // 에러 발생 시 빈 배열로 설정
        }
    };

    const handleParticipantSelect = (user) => {
        if (!selectedParticipants.find(p => p.id === user.id)) {
            setSelectedParticipants([...selectedParticipants, user]);
        }
    };

    const handleCreateMeeting = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No token found');
            }

            const meetingData = {
                ...formData,
                participants: selectedParticipants.map(p => p.id),
                host_id: localStorage.getItem('id')
            };

            await axios.post(`${API_BASE_URL}/api/meetings`, meetingData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            alert('화상 미팅이 성공적으로 생성되었습니다.');
            setShowCreateModal(false);
            fetchMeetings();
        } catch (error) {
            console.error('미팅 생성 실패:', error);
            if (error.response?.status === 401) {
                navigate('/');
            }
            alert('미팅 생성에 실패했습니다.');
        }
    };

    // 새로운 사용자가 참여했을 때
    const handleUserJoined = async (userId) => {
        const peerConnection = createPeerConnection(userId);
        
        // 로컬 스트림 추가
        if (stream) {
            stream.getTracks().forEach(track => {
                peerConnection.addTrack(track, stream);
            });
        }
        
        // Offer 생성 및 전송
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        socket.current.emit('offer', { userId, offer });
    };

    // Offer를 받았을 때
    const handleOffer = async ({ userId, offer }) => {
        const peerConnection = createPeerConnection(userId);
        await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
        
        // 로컬 스트림 추가
        if (stream) {
            stream.getTracks().forEach(track => {
                peerConnection.addTrack(track, stream);
            });
        }
        
        // Answer 생성 및 전송
        const answer = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answer);
        socket.current.emit('answer', { userId, answer });
    };

    // Answer를 받았을 때
    const handleAnswer = async ({ userId, answer }) => {
        const peerConnection = peerConnections[userId];
        if (peerConnection) {
            await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
        }
    };

    // ICE candidate를 받았을 때
    const handleIceCandidate = ({ userId, candidate }) => {
        const peerConnection = peerConnections[userId];
        if (peerConnection) {
            peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        }
    };

    // PeerConnection 생성 함수
    const createPeerConnection = (userId) => {
        const configuration = {
            iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                // TURN 서버 설정도 필요할 수 있습니다
            ]
        };
        
        const peerConnection = new RTCPeerConnection(configuration);
        
        peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                socket.current.emit('ice-candidate', {
                    userId,
                    candidate: event.candidate
                });
            }
        };
        
        peerConnection.ontrack = (event) => {
            setTelemedicineStreams(prev => ({
                ...prev,
                [userId]: event.streams[0]
            }));
        };
        
        setPeerConnections(prev => ({
            ...prev,
            [userId]: peerConnection
        }));
        
        return peerConnection;
    };

    // 화상 회의 시작 함수 수정
    const startInstantMeeting = async () => {
        const success = await initializeMedia();
        if (success) {
            setShowVideoCall(true);
            // 새로운 방 생성
            const newRoomId = Math.random().toString(36).substring(7);
            setRoomId(newRoomId);
            socket.current.emit('create-room', newRoomId);
        }
    };

    // 화상 회의 참여 함수 추가
    const joinMeeting = async (meetingRoomId) => {
        const success = await initializeMedia();
        if (success) {
            setShowVideoCall(true);
            setRoomId(meetingRoomId);
            socket.current.emit('join-room', meetingRoomId);
        }
    };

    // 회의 참여 모달 컴포넌트
    const JoinMeetingModal = () => (
        <div className="modal">
            <div className="modal-content">
                <h2>회의 참여하기</h2>
                <div className="form-group">
                    <label>회의실 ID</label>
                    <input
                        type="text"
                        value={joinRoomId}
                        onChange={(e) => setJoinRoomId(e.target.value)}
                        placeholder="회의실 ID를 입력하세요"
                    />
                </div>
                <div className="modal-buttons">
                    <button onClick={() => {
                        if (joinRoomId.trim()) {
                            joinMeeting(joinRoomId);
                            setShowJoinModal(false);
                        }
                    }}>참여하기</button>
                    <button onClick={() => setShowJoinModal(false)}>취소</button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="telemedicine-container">
            <NavigationBar />
            <div className="telemedicine-content">
                {/* 비대면 진료 신청 현황 */}
                <h2 className="telemedicine-section-title">* 비대면 진료 신청 현황</h2>
                <div className="telemedicine-table-wrapper">
                    <table className="telemedicine-table">
                        <thead>
                            <tr>
                                <th>신청일자</th>
                                <th>요청자</th>
                                <th>성별</th>
                                <th>나이</th>
                                <th>요청 구분</th>
                                <th>증상</th>
                                <th>희망일시</th>
                                <th>처리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applyList.length === 0 ? (
                                <tr><td colSpan="8" className="no-data">데이터 없음</td></tr>
                            ) : (
                                applyList.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{row.date}</td>
                                        <td>{row.name}</td>
                                        <td>{row.gender}</td>
                                        <td>{row.age}</td>
                                        <td>{row.type}</td>
                                        <td>{row.symptom}</td>
                                        <td>{row.hopeDate}</td>
                                        <td>
                                            <button className="action-btn accept">소락</button>
                                            <button className="action-btn reject">거절</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    <Paging current={applyPage} total={applyTotalPages} onChange={handleApplyPageChange} />
                </div>

                {/* 비대면 진료 현황 */}
                <h2 className="telemedicine-section-title">* 비대면 진료 현황</h2>
                <div className="telemedicine-table-wrapper">
                    <table className="telemedicine-table">
                        <thead>
                            <tr>
                                <th>신청일자</th>
                                <th>수락일자</th>
                                <th>요청자</th>
                                <th>나이</th>
                                <th>요청구분</th>
                                <th>증상</th>
                                <th>기록</th>
                            </tr>
                        </thead>
                        <tbody>
                            {statusList.length === 0 ? (
                                <tr><td colSpan="7" className="no-data">데이터 없음</td></tr>
                            ) : (
                                statusList.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{row.applyDate}</td>
                                        <td>{row.acceptDate}</td>
                                        <td>{row.name}</td>
                                        <td>{row.age}</td>
                                        <td>{row.type}</td>
                                        <td>{row.symptom}</td>
                                        <td>{row.record}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    <Paging current={statusPage} total={statusTotalPages} onChange={handleStatusPageChange} />
                </div>
            </div>
        </div>
    );
};

export default TeleMedicine;
